import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, FolderUp, Trash2, Upload } from 'lucide-react';
import { DragEvent, FormEvent, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, EmptyState, Field, Loading, Notice, PageHeader, StatusBadge } from '../components/ui';
import { api, downloadAuthenticated } from '../lib/api';
import { useAuth } from '../lib/auth';
import { uploadFileDirectly } from '../lib/direct-upload';
import { filesFromDrop } from '../lib/drop-files';
import { createProfusionArchive, summarizeProfusionFolder, type ProfusionFolderSummary } from '../lib/profusion-folder';
import { hasAnyRole, type DocumentSource, type DocumentType, type StudyDetail } from '../lib/types';

type SharePointUrlResponse = { url: string; webUrl?: string | null };

const INDIVIDUAL_DOCUMENT_TYPES: DocumentType[] = ['CAPTURA', 'VIDEO', 'SOPORTE', 'OTRO'];
const DOCUMENT_SOURCES: DocumentSource[] = ['APLICACION', 'PROFUSION', 'GENERADO', 'IMPORTADO', 'MICROSOFT_GRAPH'];
const MAX_DIRECT_UPLOAD_BYTES = 1024 * 1024 * 1024;
const DIRECT_UPLOAD_THRESHOLD_BYTES = 10 * 1024 * 1024;

type DirectUploadSession = {
  id: string;
  uploadUrl: string;
  expirationDateTime: string;
  chunkSizeBytes: number;
};

const documentTypeAccept: Partial<Record<DocumentType, string>> = {
  CAPTURA: 'image/*',
  VIDEO: 'video/*',
  SOPORTE: 'image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/zip,.zip,.pdf,.doc,.docx,.txt',
  OTRO: 'image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/zip,.zip,.pdf,.doc,.docx,.txt',
};

function suggestedTypeForFile(file: File): DocumentType | null {
  const mimeType = file.type.toLowerCase();
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  if (mimeType.startsWith('image/')) return 'CAPTURA';
  if (mimeType.startsWith('video/')) return 'VIDEO';
  if (
    mimeType === 'application/pdf' ||
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'text/plain' ||
    mimeType === 'application/zip' ||
    mimeType === 'application/x-zip-compressed' ||
    ['pdf', 'doc', 'docx', 'txt', 'zip'].includes(extension)
  ) return 'SOPORTE';
  return null;
}

export function DocumentsPage() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();
  const query = useQuery({ queryKey: ['study', id], queryFn: () => api<StudyDetail>(`/api/studies/${id}`) });
  const [type, setType] = useState<DocumentType>('CAPTURA');
  const [source, setSource] = useState<DocumentSource>('APLICACION');
  const [include, setInclude] = useState(true);
  const [quality, setQuality] = useState(false);
  const [retention, setRetention] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [profusionFiles, setProfusionFiles] = useState<File[]>([]);
  const [profusionSummary, setProfusionSummary] = useState<ProfusionFolderSummary | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [directProgress, setDirectProgress] = useState<number | null>(null);
  const [directUploadedBytes, setDirectUploadedBytes] = useState(0);
  const [directAbort, setDirectAbort] = useState<AbortController | null>(null);
  const [dragTarget, setDragTarget] = useState<'profusion' | 'individual' | null>(null);
  const [retiringDocumentId, setRetiringDocumentId] = useState<number | null>(null);
  const [retireReason, setRetireReason] = useState('');
  const folderInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  function selectProfusionFiles(selectedFiles: File[]) {
    try {
      setProfusionSummary(selectedFiles.length ? summarizeProfusionFolder(selectedFiles) : null);
      setProfusionFiles(selectedFiles);
      setMessage(null);
    } catch (error) {
      setProfusionFiles([]);
      setProfusionSummary(null);
      setMessage(error instanceof Error ? error.message : 'No fue posible leer la carpeta seleccionada.');
    }
  }

  function selectIndividualFile(selectedFile: File | null) {
    setFile(selectedFile);
    setMessage(null);
    if (!selectedFile) return;
    const suggestedType = suggestedTypeForFile(selectedFile);
    if (suggestedType && suggestedType !== type) setType(suggestedType);
  }

  async function handleDrop(event: DragEvent<HTMLDivElement>, target: 'profusion' | 'individual') {
    event.preventDefault();
    setDragTarget(null);
    const droppedFiles = await filesFromDrop(event.dataTransfer);
    if (target === 'profusion') {
      selectProfusionFiles(droppedFiles);
      return;
    }
    if (droppedFiles.length !== 1) {
      setMessage('Arrastre un solo archivo en esta zona.');
      return;
    }
    selectIndividualFile(droppedFiles[0] ?? null);
  }

  async function uploadDocument(uploadFile: File, metadata: {
    type: DocumentType;
    source: DocumentSource;
    includeInReport: boolean;
    qualityValidated: boolean;
  }) {
    const body = new FormData();
    body.append('type', metadata.type);
    body.append('source', metadata.source);
    body.append('includeInReport', String(metadata.includeInReport));
    body.append('qualityValidated', String(metadata.qualityValidated));
    if (retention) body.append('retentionUntil', retention);
    body.append('file', uploadFile);
    return api(`/api/studies/${id}/documents`, { method: 'POST', body });
  }

  async function uploadDirectDocument(uploadFile: File, metadata: {
    type: DocumentType;
    source: DocumentSource;
    includeInReport: boolean;
    qualityValidated: boolean;
  }) {
    const session = await api<DirectUploadSession>(`/api/studies/${id}/document-upload-sessions`, {
      method: 'POST',
      body: JSON.stringify({
        ...metadata,
        fileName: uploadFile.name,
        mimeType: uploadFile.type || 'application/octet-stream',
        sizeBytes: uploadFile.size,
        retentionUntil: retention || null,
      }),
    });
    const controller = new AbortController();
    setDirectAbort(controller);
    setDirectProgress(0);
    setDirectUploadedBytes(0);
    try {
      const result = await uploadFileDirectly(uploadFile, session.uploadUrl, {
        chunkSizeBytes: session.chunkSizeBytes,
        signal: controller.signal,
        onProgress: (percent, uploadedBytes) => {
          setDirectProgress(percent);
          setDirectUploadedBytes(uploadedBytes);
        },
      });
      return await api(`/api/document-upload-sessions/${session.id}/complete`, {
        method: 'POST',
        body: JSON.stringify(result),
      });
    } catch (error) {
      await fetch(session.uploadUrl, { method: 'DELETE' }).catch(() => undefined);
      await api(`/api/document-upload-sessions/${session.id}`, { method: 'DELETE' }).catch(() => undefined);
      throw error;
    } finally {
      setDirectAbort(null);
    }
  }

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Seleccione un archivo.');
      const metadata = { type, source, includeInReport: include, qualityValidated: quality };
      return file.size > DIRECT_UPLOAD_THRESHOLD_BYTES
        ? uploadDirectDocument(file, metadata)
        : uploadDocument(file, metadata);
    },
    onSuccess: async () => {
      setMessage('Documento cargado correctamente.');
      setFile(null);
      setDirectProgress(null);
      setDirectUploadedBytes(0);
      await client.invalidateQueries({ queryKey: ['study', id] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'No fue posible cargar el archivo.'),
  });

  const uploadProfusion = useMutation({
    mutationFn: async () => {
      const archive = await createProfusionArchive(profusionFiles);
      return uploadDocument(archive, {
        type: 'ESTUDIO_FUENTE',
        source: 'PROFUSION',
        includeInReport: false,
        qualityValidated: false,
      });
    },
    onSuccess: async () => {
      setMessage('Estudio fuente Profusion cargado correctamente en SharePoint.');
      setProfusionFiles([]);
      setProfusionSummary(null);
      if (folderInput.current) folderInput.current.value = '';
      await client.invalidateQueries({ queryKey: ['study', id] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'No fue posible cargar el estudio Profusion.'),
  });

  const patch = useMutation({
    mutationFn: ({ documentId, body }: { documentId: number; body: unknown }) => api(`/api/documents/${documentId}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),
    onSuccess: async () => client.invalidateQueries({ queryKey: ['study', id] }),
  });

  const retire = useMutation({
    mutationFn: ({ documentId, reason }: { documentId: number; reason: string }) => api(`/api/documents/${documentId}/retire`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
    onSuccess: async () => {
      setRetiringDocumentId(null);
      setRetireReason('');
      setMessage('Documento retirado correctamente. La trazabilidad se conserva.');
      await client.invalidateQueries({ queryKey: ['study', id] });
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : 'No fue posible retirar el documento.'),
  });

  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <Notice kind="error">No fue posible cargar los documentos.</Notice>;

  const study = query.data;
  const canManage = hasAnyRole(user, ['TECNICA', 'MEDICO', 'COORDINACION', 'ADMINISTRADOR']);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (file && file.size > MAX_DIRECT_UPLOAD_BYTES) {
      setMessage('El archivo supera el límite permitido de 1 GB.');
      return;
    }
    if (file && type === 'CAPTURA' && !file.type.toLowerCase().startsWith('image/')) {
      setMessage('El tipo CAPTURA solo admite imágenes. Para PDF, Word, ZIP o texto use SOPORTE u OTRO.');
      return;
    }
    if (file && type === 'VIDEO' && !file.type.toLowerCase().startsWith('video/')) {
      setMessage('El tipo VIDEO solo admite archivos de video. Para PDF, Word, ZIP o texto use SOPORTE u OTRO.');
      return;
    }
    upload.mutate();
  };

  return <>
    <PageHeader
      title={`Documentos · ${study.code}`}
      description="Estudios fuente Profusion, capturas, anexos e informes almacenados localmente o en SharePoint."
      actions={<StatusBadge value={study.generationStatus} />}
    />
    {message && <Notice kind={message.includes('correctamente') ? 'success' : 'error'} onClose={() => setMessage(null)}>{message}</Notice>}
    {canManage && <>
      <Card title="Estudio fuente Profusion">
        <p className="muted">Seleccione la carpeta completa generada por Profusion. La plataforma la empaqueta automáticamente y la almacena como un único estudio fuente en SharePoint.</p>
        <div className="form-grid three">
          <div className="field">
            <span>Carpeta del estudio<strong> *</strong></span>
            <div
              className={`document-drop-zone ${dragTarget === 'profusion' ? 'drag-active' : ''}`}
              onDragEnter={(event) => { event.preventDefault(); setDragTarget('profusion'); }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragTarget(null)}
              onDrop={(event) => void handleDrop(event, 'profusion')}
            >
              <FolderUp size={24} />
              <span><strong>Arrastre la carpeta Profusion aquí</strong><small>o selecciónela desde el equipo</small></span>
              <Button type="button" variant="secondary" onClick={() => folderInput.current?.click()}>Elegir carpeta</Button>
              <input
                ref={folderInput}
                className="document-file-input"
                aria-label="Carpeta del estudio"
                type="file"
                multiple
                {...{ webkitdirectory: '', directory: '' }}
                onChange={(event) => selectProfusionFiles(Array.from(event.target.files ?? []))}
              />
            </div>
          </div>
          <Field label="Tipo"><input value="ESTUDIO_FUENTE" readOnly /></Field>
          <Field label="Origen"><input value="PROFUSION" readOnly /></Field>
        </div>
        {profusionSummary && <div className="compact-list">
          <div className="compact-row vertical">
            <strong>{profusionSummary.folderName}</strong>
            <small>{profusionSummary.fileCount} archivos · {formatBytes(profusionSummary.totalBytes)}</small>
          </div>
        </div>}
        <Button disabled={!profusionFiles.length} busy={uploadProfusion.isPending} onClick={() => uploadProfusion.mutate()}>
          <FolderUp size={16} /> Cargar estudio Profusion
        </Button>
      </Card>
      <Card title="Cargar archivo individual">
        <form onSubmit={submit}>
          <div className="form-grid three">
            <Field label="Tipo" required>
              <select value={type} onChange={(event) => setType(event.target.value as DocumentType)}>
                {INDIVIDUAL_DOCUMENT_TYPES.map((value) => <option key={value}>{value}</option>)}
              </select>
              <small>{type === 'CAPTURA' ? 'Use CAPTURA para imágenes clínicas. Los PDF y documentos se cargan como SOPORTE u OTRO.' : 'Archivos grandes se transfieren directamente a SharePoint. Máximo 1 GB.'}</small>
            </Field>
            <Field label="Origen">
              <select value={source} onChange={(event) => setSource(event.target.value as DocumentSource)}>
                {DOCUMENT_SOURCES.map((value) => <option key={value}>{value}</option>)}
              </select>
            </Field>
            <Field label="Retención hasta"><input type="date" value={retention} onChange={(event) => setRetention(event.target.value)} /></Field>
            <div className="field">
              <span>Archivo<strong> *</strong></span>
              <div
                className={`document-drop-zone compact ${dragTarget === 'individual' ? 'drag-active' : ''}`}
                onDragEnter={(event) => { event.preventDefault(); setDragTarget('individual'); }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setDragTarget(null)}
                onDrop={(event) => void handleDrop(event, 'individual')}
              >
                <Upload size={22} />
                <span><strong>{file?.name ?? 'Arrastre un archivo aquí'}</strong><small>{file ? formatBytes(file.size) : 'o selecciónelo desde el equipo'}</small></span>
                <Button type="button" variant="secondary" onClick={() => fileInput.current?.click()}>Seleccionar</Button>
                <input
                  ref={fileInput}
                  className="document-file-input"
                  aria-label="Archivo"
                  type="file"
                  accept={documentTypeAccept[type]}
                  onChange={(event) => selectIndividualFile(event.target.files?.[0] ?? null)}
                />
              </div>
            </div>
          </div>
          <div className="checkbox-grid">
            <label><input type="checkbox" checked={include} onChange={(event) => setInclude(event.target.checked)} /> Incluir en informe</label>
            <label><input type="checkbox" checked={quality} onChange={(event) => setQuality(event.target.checked)} /> Calidad validada</label>
          </div>
          {directProgress !== null && <div className="upload-progress" aria-live="polite">
            <progress max="100" value={directProgress} />
            <span>{directProgress}% · {formatBytes(directUploadedBytes)} de {formatBytes(file?.size ?? 0)}</span>
          </div>}
          <div className="button-row">
            <Button type="submit" busy={upload.isPending}><Upload size={16} /> Cargar documento</Button>
            {directAbort && <Button type="button" variant="secondary" onClick={() => directAbort.abort()}>Cancelar carga</Button>}
          </div>
        </form>
      </Card>
    </>}
    <Card title={`Repositorio del estudio (${study.documents.length})`}>
      {!study.documents.length
        ? <EmptyState title="Sin documentos" description="Todavía no se han cargado archivos para este estudio." />
        : <div className="document-grid">{study.documents.map((document) => <article className="document-card" key={document.id}>
          <div>
            <StatusBadge value={document.type} />
            <strong>{document.fileName}</strong>
            <small>{document.mimeType} · {formatBytes(document.sizeBytes)}{document.reportVersion ? ` · informe v${document.reportVersion}` : ''}</small>
            <small>{document.storageProvider}{document.templateVersion ? ` · plantilla ${document.templateVersion}` : ''}{document.driveItemId ? ' · Referencia SharePoint registrada' : ''}</small>
          </div>
          {canManage && <div className="checkbox-grid compact">
            <label><input type="checkbox" checked={document.includeInReport} onChange={(event) => patch.mutate({ documentId: document.id, body: { includeInReport: event.target.checked } })} /> Informe</label>
            <label><input type="checkbox" checked={document.qualityValidated} onChange={(event) => patch.mutate({ documentId: document.id, body: { qualityValidated: event.target.checked } })} /> Validado</label>
          </div>}
          <div className="button-row">
            <Button variant="secondary" onClick={() => void downloadAuthenticated(`/api/documents/${document.id}/download`, document.fileName)}><Download size={15} /> Descargar</Button>
            {document.storageProvider === 'SHAREPOINT' && <Button variant="ghost" onClick={async () => {
              const result = await api<SharePointUrlResponse>(`/api/documents/${document.id}/url`);
              window.open(result.webUrl ?? result.url, '_blank', 'noopener,noreferrer');
            }}><ExternalLink size={15} /> Abrir en SharePoint</Button>}
            {canManage && ['CAPTURA', 'ESTUDIO_FUENTE', 'VIDEO', 'SOPORTE', 'OTRO'].includes(document.type) && <Button
              variant="ghost"
              onClick={() => {
                setRetiringDocumentId(document.id);
                setRetireReason('');
              }}
            ><Trash2 size={15} /> Retirar</Button>}
          </div>
          {retiringDocumentId === document.id && <div className="document-retire-panel">
            <label>Motivo del retiro
              <textarea
                rows={2}
                maxLength={500}
                value={retireReason}
                onChange={(event) => setRetireReason(event.target.value)}
                placeholder="Ej. Archivo cargado en el estudio equivocado"
              />
            </label>
            <small>El archivo dejará de mostrarse y no se incluirá en el informe. Se conservará para auditoría.</small>
            <div className="button-row">
              <Button
                variant="danger"
                busy={retire.isPending}
                disabled={retireReason.trim().length < 5}
                onClick={() => retire.mutate({ documentId: document.id, reason: retireReason.trim() })}
              >Confirmar retiro</Button>
              <Button variant="secondary" onClick={() => setRetiringDocumentId(null)}>Cancelar</Button>
            </div>
          </div>}
        </article>)}</div>}
    </Card>
    <div className="sticky-actions"><Button variant="secondary" onClick={() => navigate(`/studies/${id}`)}>Volver</Button></div>
  </>;
}

function formatBytes(value: string | number | null) {
  if (value == null) return 'Tamaño no registrado';
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return String(value);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}
